import { Simulation } from '@/hooks/useSimulation';
import {
  CurrencyCodeType,
  DashboardOperation,
  FundingMethodItem,
  PaymentRailType,
} from '@/types/currency';
import { GenericAction } from '@/utility/reducers';

export interface TransferFlowState {
  readonly fundingMethod: FundingMethodItem;
  readonly fromAmount: number | null;
  readonly toAmount: number | null;
  readonly selectedOperation: DashboardOperation | null;
  readonly recipientAccountId: string;
  readonly paymentReference: string;
  readonly transferPurpose: string;
}

export enum TransferFlowActionType {
  transactionFlowChanged = 'transactionFlowChanged',
  recipientAccountChanged = 'recipientAccountChanged',
  fromAmountChanged = 'fromAmountChanged',
  toAmountChanged = 'toAmountChanged',
  fundingMethodChanged = 'fundingMethodChanged',
  paymentReferenceChanged = 'paymentRefereceChanged',
  transferPurposeChanged = 'transferPurposeChanged',
  simulationCreated = 'simulationCreated',
}

export type TransactionFlowChangedAction = GenericAction<
  TransferFlowActionType.transactionFlowChanged,
  DashboardOperation
>;

export type RecipientAccountChangedAction = GenericAction<
  TransferFlowActionType.recipientAccountChanged,
  string
>;

export type FromAmountChangedAction = GenericAction<
  TransferFlowActionType.fromAmountChanged,
  number | null
>;

export type ToAmountChangedAction = GenericAction<
  TransferFlowActionType.toAmountChanged,
  number | null
>;

export type FundingMethodChangedAction = GenericAction<
  TransferFlowActionType.fundingMethodChanged,
  FundingMethodItem
>;

export type PaymentReferenceChangedAction = GenericAction<
  TransferFlowActionType.paymentReferenceChanged,
  string
>;

export type TransferPurposeChangedAction = GenericAction<
  TransferFlowActionType.transferPurposeChanged,
  string
>;

export type SimulationCreatedAction = GenericAction<
  TransferFlowActionType.simulationCreated,
  Simulation
>;

export type TransferFlowAction =
  | TransactionFlowChangedAction
  | RecipientAccountChangedAction
  | FromAmountChangedAction
  | ToAmountChangedAction
  | FundingMethodChangedAction
  | PaymentReferenceChangedAction
  | TransferPurposeChangedAction
  | SimulationCreatedAction;

export const initialState: TransferFlowState = {
  fundingMethod: {
    id: '',
    method: PaymentRailType.unset,
    currency: CurrencyCodeType.UNSET,
  },
  fromAmount: null,
  toAmount: null,
  selectedOperation: null,
  recipientAccountId: '',
  paymentReference: '',
  transferPurpose: '',
};

export const applySimulation = (
  state: TransferFlowState,
  simulation: Simulation,
): TransferFlowState => {
  const { from, to } = simulation;

  return {
    ...state,
    fromAmount: state.fromAmount ?? Number(from.value),
    toAmount: state.toAmount ?? Number(to.value),
  };
};

export const transferFlowReducer = (
  state: TransferFlowState,
  action: TransferFlowAction,
): TransferFlowState => {
  switch (action.type) {
    case TransferFlowActionType.transactionFlowChanged:
      return { ...state, selectedOperation: action.payload };
    case TransferFlowActionType.recipientAccountChanged:
      return {
        ...initialState,
        recipientAccountId: action.payload,
        fundingMethod: {
          id: '',
          method: PaymentRailType.unset,
          currency: CurrencyCodeType.UNSET,
        },
      };
    case TransferFlowActionType.fromAmountChanged:
      return { ...state, fromAmount: action.payload, toAmount: null };
    case TransferFlowActionType.toAmountChanged:
      return { ...state, toAmount: action.payload, fromAmount: null };
    case TransferFlowActionType.fundingMethodChanged:
      return { ...state, fundingMethod: action.payload };
    case TransferFlowActionType.transferPurposeChanged:
      return { ...state, transferPurpose: action.payload };
    case TransferFlowActionType.paymentReferenceChanged:
      return { ...state, paymentReference: action.payload };
    case TransferFlowActionType.simulationCreated:
      return applySimulation(state, action.payload);
    default:
      return state;
  }
};

export const transactionFlowChanged = (
  payload: DashboardOperation,
): TransactionFlowChangedAction => ({
  type: TransferFlowActionType.transactionFlowChanged,
  payload: payload,
});

export const recipientAccountChanged = (payload: string): RecipientAccountChangedAction => ({
  type: TransferFlowActionType.recipientAccountChanged,
  payload: payload,
});

export const fromAmountChanged = (payload: number | null): FromAmountChangedAction => ({
  type: TransferFlowActionType.fromAmountChanged,
  payload: payload,
});

export const toAmountChanged = (payload: number | null): ToAmountChangedAction => ({
  type: TransferFlowActionType.toAmountChanged,
  payload: payload,
});

export const fundingMethodChanged = (payload: FundingMethodItem): FundingMethodChangedAction => ({
  type: TransferFlowActionType.fundingMethodChanged,
  payload: payload,
});

export const paymentReferenceChanged = (payload: string): PaymentReferenceChangedAction => ({
  type: TransferFlowActionType.paymentReferenceChanged,
  payload: payload,
});

export const transferPurposeChanged = (payload: string): TransferPurposeChangedAction => ({
  type: TransferFlowActionType.transferPurposeChanged,
  payload: payload,
});

export const simulationCreated = (payload: Simulation): SimulationCreatedAction => ({
  type: TransferFlowActionType.simulationCreated,
  payload: payload,
});
